/***
 *
 *   DROPDOWN
 *   Menu surface. Styling matches
 *   ui-reference/src/components/ui/dropdown-menu.tsx.
 *
 *   Opening, positioning and click-outside are the caller's job via
 *   components/hooks/attach. What Radix gave the reference and this has to
 *   provide by hand is the keyboard model:
 *
 *     - the first item takes focus when the menu opens
 *     - Arrow keys move between items and wrap at the ends
 *     - Home / End jump to the first / last item
 *     - Enter and Space activate
 *     - role="menu" / role="menuitem" so it is announced as a menu
 *
 *   PROPS
 *   list: array of { label, action, icon, disabled, checked, shortcut }
 *         or { separator: true } / { label, heading: true }
 *
 **********/

import { useEffect, useRef } from 'react';
import { Icon, cn } from 'components/lib';
import Style from './dropdown.tailwind';

const Dropdown = (props) => {
    const { list } = props;
    const menuRef = useRef(null);

    const items = () => Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);

    useEffect(() => {
        // Opening the menu moves focus into it, so the keyboard model below
        // has somewhere to start.
        const id = requestAnimationFrame(() => items()[0]?.focus());
        return () => cancelAnimationFrame(id);
    }, []);

    function onKeyDown(e) {
        const all = items();
        if (!all.length) return;

        const index = all.indexOf(document.activeElement);

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                all[(index + 1 + all.length) % all.length].focus();
                break;
            case 'ArrowUp':
                e.preventDefault();
                all[(index - 1 + all.length) % all.length].focus();
                break;
            case 'Home':
                e.preventDefault();
                all[0].focus();
                break;
            case 'End':
                e.preventDefault();
                all[all.length - 1].focus();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                document.activeElement?.click();
                break;
            default:
                break;
        }
    }

    return (
        <div ref={menuRef} role="menu" className={Style.dropdown} onKeyDown={onKeyDown}>
            {list.map((item, index) => {
                if (item.separator) return <div key={`sep-${index}`} className={Style.separator} role="separator" />;

                if (item.heading)
                    return (
                        <div key={`head-${index}`} className={Style.label}>
                            {item.label}
                        </div>
                    );

                return (
                    <div
                        key={index}
                        role="menuitem"
                        tabIndex={-1}
                        aria-disabled={item.disabled || undefined}
                        className={cn(Style.item, item.disabled && Style.itemDisabled)}
                        onClick={item.disabled ? undefined : item.action}>
                        {item.icon && <Icon image={item.icon} size={16} color="currentColor" />}
                        {item.label}
                        {item.shortcut && <span className={Style.shortcut}>{item.shortcut}</span>}
                        {item.checked && <Icon image="check" size={16} color="currentColor" className={Style.check} />}
                    </div>
                );
            })}
        </div>
    );
};

export default Dropdown;
